import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import HeroSection from "@/components/sections/HeroSection";
import ServicesSection from "@/components/sections/ServicesSection";
import DirectorSection from "@/components/sections/DirectorSection";
import SectorsSection from "@/components/sections/SectorsSection";
import PricingSection from "@/components/sections/PricingSection";
import TestimonialsSection from "@/components/sections/TestimonialsSection";
import FAQSection from "@/components/sections/FAQSection";
import ContactSection from "@/components/sections/ContactSection";
import Footer from "@/components/sections/Footer";
import BrandBanner from "@/components/sections/BrandBanner";
import ProductsSection from "@/components/sections/ProductsSection";
import VersionComparisonSection from "@/components/sections/VersionComparisonSection";
import EcfSection from "@/components/sections/EcfSection";
import Navbar from "@/components/Navbar";
import CheckoutModal from "@/components/CheckoutModal";
import PaymentStatusModal from "@/components/PaymentStatusModal";
import CursorGlow from "@/components/effects/CursorGlow";
import ScrollProgress from "@/components/effects/ScrollProgress";
import SectionDivider from "@/components/effects/SectionDivider";
import BrandWatermark from "@/components/effects/BrandWatermark";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LandingPage = () => {
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get("payment_status");
    const sessionId = urlParams.get("session_id");
    if (status && sessionId) {
      setPaymentStatus({ status, sessionId });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    fetchContent();
  }, [fetchContent]);

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setIsCheckoutOpen(true);
  };

  const handleCheckout = async (email) => {
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: selectedPlan,
        origin_url: window.location.origin,
        email: email
      });
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      // handled silently
      toast.error("Error al procesar el pago. Intente nuevamente.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#02040a] flex items-center justify-center">
        <motion.div
          className="flex flex-col items-center gap-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          <div className="relative">
            <div className="w-12 h-12 border-2 border-[#3b82f6]/30 rounded-full" />
            <div className="w-12 h-12 border-2 border-[#3b82f6] border-t-transparent rounded-full animate-spin absolute inset-0" />
          </div>
          <p className="text-[#94a3b8] text-sm tracking-wider uppercase">Cargando...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="landing-page" className="min-h-screen bg-[#02040a] relative">
      <SEO />
      <ScrollProgress />
      <CursorGlow />
      <Navbar />
      <BrandWatermark />
      
      {/* Noise overlay for texture */}
      <div className="noise-overlay" />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <HeroSection content={content?.hero} onSelectPlan={handleSelectPlan} />
        <SectionDivider />
        <ProductsSection />
        <VersionComparisonSection />
        <BrandBanner />
        <ServicesSection content={content?.services} />
        <BrandBanner />
        <DirectorSection content={content?.team} />
        <SectionDivider />
        <SectorsSection content={content?.sectors} />
        <SectionDivider />
        <PricingSection content={content?.pricing} onSelectPlan={handleSelectPlan} />
        <EcfSection />
        <BrandBanner />
        <TestimonialsSection content={content?.testimonials} />
        <SectionDivider />
        <FAQSection content={content?.faq} />
        <SectionDivider />
        <ContactSection content={content?.contact} />
        <Footer content={content?.contact} />
      </motion.div>

      <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        planId={selectedPlan}
        plans={content?.pricing?.plans}
        onCheckout={handleCheckout}
      />

      {paymentStatus && (
        <PaymentStatusModal
          status={paymentStatus.status}
          sessionId={paymentStatus.sessionId}
          onClose={() => setPaymentStatus(null)}
        />
      )}
    </div>
  );
};

export default LandingPage;
